import { useEffect, useState } from 'react';
import { FAB_AVOID_ATTRIBUTE, FAB_AVOID_SELECTOR, zoneRootMargin } from '../../lib/fabClearance';

/**
 * useFabPracticeClearance (#3633, D-203 option a): true while at least one
 * element carrying `data-fab-avoid` overlaps the FabMenu trigger's zone, so
 * FabMenu can hide the trigger instead of covering a Practice button.
 * lib/fabClearance.js holds the decision, the deliberately narrow "zone"
 * reading, and the marker contract.
 *
 * MECHANISM: one IntersectionObserver whose root is the viewport shrunk to the
 * trigger's zone (rootMargin), observing every marked element. IO fires on any
 * change in geometry, which covers the cases a scroll listener misses: a
 * carousel scrolled SIDEWAYS (no window scroll event, scrollTop stays 0) and
 * layout shift after an image or font loads.
 *
 * LATE MOUNTS: marked elements appear and disappear (async data, modals, route
 * changes). A MutationObserver on <body> re-scans on childList and on the
 * marker attribute. Elements that left the document are unobserved and dropped
 * from the intersecting set in the same pass, so a Practice button that
 * unmounts while inside the zone releases the trigger.
 *
 * RESIZE: rootMargin is immutable, so on resize / orientation change the
 * IntersectionObserver is rebuilt against the trigger's freshly measured box. The
 * rebuild KEEPS the previous covered answer until the replacement observer's first
 * callback (#3686): clearing it first made the disc flash over the button it was
 * clearing every time iOS Safari's collapsing URL bar fired a resize. The answer is
 * only dropped at once when nothing can be observed (see FAIL-OPEN).
 *
 * FAIL-OPEN: no IntersectionObserver / MutationObserver, or an unmeasurable
 * trigger, means `false` (the trigger stays visible: the pre-#3633 behaviour).
 * A missing capability must never hide help and bug-report.
 *
 * @param {object} args
 * @param {boolean} args.enabled  phone breakpoint on, not a /chat route, trigger rendered
 * @param {{current: (HTMLElement|null)}} args.triggerRef  the trigger button
 * @returns {boolean} covered
 */
export default function useFabPracticeClearance({ enabled, triggerRef }) {
  const [covered, setCovered] = useState(false);

  useEffect(() => {
    if (!enabled) { setCovered(false); return undefined; }
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    if (typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined') return undefined;

    /** @type {Set<Element>} every marked element currently observed */
    const tracked = new Set();
    /** @type {Set<Element>} the subset intersecting the zone right now */
    const intersecting = new Set();
    let io = null;
    let scanQueued = false;
    let disposed = false;
    // #3686: true from a rebuild until the REPLACEMENT observer's first callback. Until then
    // `intersecting` still holds the previous observer's answer on purpose (see buildObserver).
    let awaitingFirstReport = false;

    const publish = () => { if (!disposed) setCovered(intersecting.size > 0); };

    // Nothing can be observed (no trigger, unmeasurable trigger, observer threw, or nothing marked):
    // that is "not covered", published at once. This is the fail-OPEN answer, never a held one.
    const publishNotCovered = () => {
      intersecting.clear();
      awaitingFirstReport = false;
      publish();
    };

    const buildObserver = () => {
      if (io) { io.disconnect(); io = null; }
      const trigger = triggerRef.current;
      if (!trigger) { publishNotCovered(); return; }
      const box = trigger.getBoundingClientRect();
      const de = document.documentElement;
      const margin = zoneRootMargin(box, { width: de.clientWidth, height: de.clientHeight });
      if (!margin) { publishNotCovered(); return; }
      // #3686 FLICKER: do NOT clear `intersecting` / publish here. This runs on every window
      // `resize` (iOS Safari fires one when the URL bar collapses mid-scroll); clearing and
      // publishing covered=false before the replacement observer has delivered its first callback
      // showed the disc for a frame or more over the button it was clearing (true -> false -> true).
      // The previous answer stands until the new observer reports; the first callback replaces it.
      // A new IntersectionObserver always delivers an initial entry for every observed target.
      awaitingFirstReport = tracked.size > 0;
      try {
        io = new IntersectionObserver((entries) => {
          if (awaitingFirstReport) { intersecting.clear(); awaitingFirstReport = false; }
          for (const entry of entries) {
            if (entry.isIntersecting && entry.target.isConnected && entry.target.matches(FAB_AVOID_SELECTOR)) intersecting.add(entry.target);
            else intersecting.delete(entry.target);
          }
          publish();
        }, { rootMargin: margin, threshold: 0 });
        tracked.forEach((el) => io.observe(el));
      } catch (err) {
        // A cosmetic clearance must never take the app down (this runs in an effect at the app root,
        // so a throw would reach the root error boundary). Fail OPEN: the trigger stays visible, which
        // is the pre-#3633 behaviour. Not silent: the warning lands in the console (and Sentry's
        // console breadcrumbs), the only reader this has.
        console.warn('[FabMenu] Practice-button clearance disabled:', err);
        io = null;
        publishNotCovered();
        return;
      }
      // Nothing marked yet: no observer callback will ever arrive to replace the old answer, and
      // there is nothing to cover, so publish not-covered now rather than hold a stale `true`.
      if (tracked.size === 0) publishNotCovered();
    };

    const scan = () => {
      scanQueued = false;
      if (disposed) return;
      let changed = false;
      tracked.forEach((el) => {
        // Gone from the document, or still mounted but no longer marked (a CTA whose
        // type changed drops the attribute): either way it stops counting.
        if (!el.isConnected || !el.matches(FAB_AVOID_SELECTOR)) {
          tracked.delete(el);
          intersecting.delete(el);
          if (io) io.unobserve(el);
          changed = true;
        }
      });
      document.querySelectorAll(FAB_AVOID_SELECTOR).forEach((el) => {
        if (!tracked.has(el)) {
          tracked.add(el);
          if (io) io.observe(el);
        }
      });
      if (changed) publish();
    };

    const queueScan = () => {
      if (scanQueued) return;
      scanQueued = true;
      queueMicrotask(scan);
    };

    const mo = new MutationObserver(queueScan);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [FAB_AVOID_ATTRIBUTE],
    });

    buildObserver();
    scan();

    window.addEventListener('resize', buildObserver);
    window.addEventListener('orientationchange', buildObserver);

    return () => {
      disposed = true;
      mo.disconnect();
      if (io) io.disconnect();
      window.removeEventListener('resize', buildObserver);
      window.removeEventListener('orientationchange', buildObserver);
      tracked.clear();
      intersecting.clear();
    };
  }, [enabled, triggerRef]);

  return covered;
}
